import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
} from "@angular/core";
import { ActionTrackerService } from "../../../shared/services/action-tracker.service";
import textConfiguration from "../../../../assets/static-text-configuration.json";
import { RiskConvergenceService } from "../../../shared/services/risk-convergence.service";

interface HistoryRecord {
  history_id: number;
  history_date: string;
  history_user: any;
  title?: string;
  description?: string;
  status: string;
  due_date?: string;
  type_id?: number;
  comments?: string;
  assignee?: any[];
  plans?: any[];
  file_changes?: FileChange[];
  // API has a typo in some records; handle both spellings
  history_entity?: "action" | "compliance";
  history_enitity?: "action" | "compliance";
  // Why the record was written, when the API says, e.g. "Moved to the
  // obligation's new version" when a compliance task moves to a new version.
  history_change_reason?: string | null;
}

interface FileChange {
  id: number;
  file_name: string;
  action: "added" | "removed";
  created_at: string;
  created_by: number;
  created_by_detail: any;
  s3_path: string;
}

interface Change {
  field: string;
  oldValue: any;
  newValue: any;
  type?: "field" | "file";
  files?: FileChange[];
}

interface ActivityEntry {
  timestamp: string;
  user: any;
  type: "change" | "comment";
  changes?: Change[];
  comment?: string;
  entityType: "action" | "compliance" | "risk";
  reason?: string | null;
}

@Component({
  selector: "app-action-history",
  templateUrl: "./action-history.component.html",
  styleUrls: ["./action-history.component.scss"],
})
export class ActionHistoryComponent implements OnInit, OnChanges {
  @Input() actionId: number | null = null;
  /** When 'compliance', shows both action + compliance entries combined.
   *  When 'action' (default), only shows action entity diffs (backward compatible).
   *  When 'risk', fetches risk history which is already pre-formatted. */
  @Input() historyType: "action" | "compliance" | "risk" = "action";

  commonText = (textConfiguration as any).common;
  activityEntries: ActivityEntry[] = [];
  loading = false;

  constructor(
    private actionTrackerService: ActionTrackerService,
    private riskConvergenceService: RiskConvergenceService
  ) {}

  ngOnInit(): void {
    if (this.actionId) {
      this.loadActivity();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["actionId"] && this.actionId) {
      this.loadActivity();
    }
  }

  loadActivity() {
    if (!this.actionId) return;
    this.loading = true;

    if (this.historyType === "risk") {
      this.riskConvergenceService.getRiskHistory(this.actionId).subscribe(
        (data: any) => {
          this.activityEntries = data.history || [];
          this.loading = false;
        },
        (err) => {
          console.error("Error loading risk activity", err);
          this.loading = false;
        }
      );
    } else {
      this.actionTrackerService
        .getActionHistory(this.actionId, this.historyType)
        .subscribe(
          (data: any) => {
            this.activityEntries = this.processHistory(data.history || []);
            this.loading = false;
          },
          (err) => {
            console.error("Error loading activity", err);
            this.loading = false;
          }
        );
    }
  }

  /** Resolve entity type handling both spellings from the API */
  private getEntityType(record: HistoryRecord): "action" | "compliance" {
    return record.history_entity || record.history_enitity || "action";
  }

  processHistory(records: HistoryRecord[]): ActivityEntry[] {
    if (!records || records.length === 0) return [];

    // Sort ascending so we can diff i against i-1 within each entity group
    records.sort(
      (a, b) =>
        new Date(a.history_date).getTime() - new Date(b.history_date).getTime()
    );

    const actionRecords = records.filter(
      (r) => this.getEntityType(r) === "action"
    );
    const complianceRecords = records.filter(
      (r) => this.getEntityType(r) === "compliance"
    );

    const entries: ActivityEntry[] = [
      ...this.processActionRecords(actionRecords),
      ...(this.historyType === "compliance"
        ? this.processComplianceRecords(complianceRecords)
        : []),
    ];

    // Sort newest first for display
    entries.sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );

    return entries;
  }

  private processActionRecords(records: HistoryRecord[]): ActivityEntry[] {
    const historyEntries: ActivityEntry[] = [];

    for (let i = 1; i < records.length; i++) {
      const current = records[i];
      const previous = records[i - 1];
      const changes = this.computeChanges(previous, current);
      const fileChanges = this.computeFileChanges(current.file_changes || []);

      const allChanges = [...changes, ...fileChanges];

      if (allChanges.length > 0) {
        historyEntries.push({
          timestamp: current.history_date,
          user: current.history_user,
          type: "change",
          changes: allChanges,
          entityType: "action",
          reason: current.history_change_reason || null,
        });
      }
    }

    // The first record is the creation event
    if (records.length > 0) {
      const first = records[0];
      historyEntries.push({
        timestamp: first.history_date,
        user: first.history_user,
        type: "change",
        changes: [
          { field: "Created", oldValue: null, newValue: "Action created" },
        ],
        entityType: "action",
      });
    }

    return historyEntries;
  }

  private processComplianceRecords(records: HistoryRecord[]): ActivityEntry[] {
    const historyEntries: ActivityEntry[] = [];

    for (let i = 0; i < records.length; i++) {
      const current = records[i];
      const previous = i > 0 ? records[i - 1] : null;

      const changes: Change[] = [];

      if (!previous) {
        // First compliance record – show as initial status
        changes.push({
          field: "Status",
          oldValue: null,
          newValue: current.status,
        });
      } else if (previous.status !== current.status) {
        changes.push({
          field: "Status",
          oldValue: previous.status,
          newValue: current.status,
        });
      }

      if (changes.length > 0) {
        historyEntries.push({
          timestamp: current.history_date,
          user: current.history_user,
          type: "change",
          changes,
          entityType: "compliance",
        });
      }
    }

    return historyEntries;
  }

  computeChanges(oldRecord: HistoryRecord, newRecord: HistoryRecord): Change[] {
    const changes: Change[] = [];
    const fields = [
      "title",
      "description",
      "status",
      "due_date",
      "type_id",
      "comments",
    ];

    fields.forEach((field) => {
      if ((oldRecord as any)[field] !== (newRecord as any)[field]) {
        if (field === "comments") {
          changes.push({
            field: this.formatFieldName(field),
            oldValue: null,
            newValue: (newRecord as any)[field],
          });
        } else {
          changes.push({
            field: this.formatFieldName(field),
            oldValue: (oldRecord as any)[field],
            newValue: (newRecord as any)[field],
          });
        }
      }
    });

    // Handle ManyToMany: assignee
    const oldAssigneeIds = oldRecord.assignee
      ? oldRecord.assignee.map((u: any) => u.id)
      : [];
    const newAssigneeIds = newRecord.assignee
      ? newRecord.assignee.map((u: any) => u.id)
      : [];
    if (
      JSON.stringify(oldAssigneeIds.sort()) !==
      JSON.stringify(newAssigneeIds.sort())
    ) {
      changes.push({
        field: "Assignee",
        oldValue: oldRecord.assignee
          ? oldRecord.assignee
              .map((u: any) => u.first_name + " " + u.last_name)
              .join(", ")
          : "",
        newValue: newRecord.assignee
          ? newRecord.assignee
              .map((u: any) => u.first_name + " " + u.last_name)
              .join(", ")
          : "",
      });
    }

    // Handle plans
    const oldPlanIds = oldRecord.plans
      ? oldRecord.plans.map((p: any) => p.id)
      : [];
    const newPlanIds = newRecord.plans
      ? newRecord.plans.map((p: any) => p.id)
      : [];
    if (
      JSON.stringify(oldPlanIds.sort()) !== JSON.stringify(newPlanIds.sort())
    ) {
      changes.push({
        field: "Plans",
        oldValue: oldRecord.plans
          ? oldRecord.plans.map((p: any) => p.display_name).join(", ")
          : "",
        newValue: newRecord.plans
          ? newRecord.plans.map((p: any) => p.display_name).join(", ")
          : "",
      });
    }

    return changes;
  }

  computeFileChanges(fileChanges: FileChange[]): Change[] {
    if (!fileChanges || fileChanges.length === 0) return [];

    return [
      {
        field: "Files",
        oldValue: null,
        newValue: null,
        type: "file",
        files: fileChanges,
      },
    ];
  }

  downloadHistoryFile(actionId: number, fileId: number) {
    this.actionTrackerService.downloadHistoryFile(actionId, fileId).subscribe(
      (res: any) => {
        const downloadLink = document.createElement("a");
        downloadLink.href = window.URL.createObjectURL(res);
        const fileChange = this.findFileById(fileId);
        downloadLink.setAttribute("download", fileChange?.file_name || "file");
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      },
      (err: any) => {
        console.error("Error downloading file", err);
      }
    );
  }

  findFileById(fileId: number): FileChange | undefined {
    for (const entry of this.activityEntries) {
      if (entry.changes) {
        for (const change of entry.changes) {
          if (change.files) {
            const file = change.files.find((f) => f.id === fileId);
            if (file) return file;
          }
        }
      }
    }
    return undefined;
  }

  formatFieldName(field: string): string {
    const names: { [key: string]: string } = {
      title: "Title",
      description: "Description",
      status: "Status",
      due_date: "Due Date",
      type_id: "Type ID",
      comments: "Comment",
    };
    return names[field] || field;
  }

  getInitials(user: any): string {
    if (!user) return "U";
    const firstName = user.first_name || "";
    const lastName = user.last_name || "";
    return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
  }

  getRelativeTime(timestamp: string): string {
    const date = new Date(timestamp);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return "just now";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return date.toLocaleDateString();
  }
}
